import {
  AdditionalRegFields,
  CreatedByOptions,
  OrganisationInfo,
  RegistrationDto,
  RegistrationProduct,
  RegPersonInfo
} from "./types";
import { Nullable } from "../../types";
import EventHelper from "../../network/helpers/event/EventHelper";
import { FormValues as INewBadge } from "../../../components/createBadge/utils/types";
import dayjs from "../../dayjs";
import StringUtils from "../../utils/stringUtils";
import { Preferences, RegistrationPayload } from "../../../components/createBadge/form/CreateBadgeForm.types";

class RegistrationDtoBuilder {
  private personInfo?: RegPersonInfo;
  private createdBy?: CreatedByOptions;
  private products?: RegistrationProduct[];
  private additionalRegFields?: Nullable<AdditionalRegFields>;
  private organisationInfo?: OrganisationInfo;

  setCreatedBy(info: CreatedByOptions) {
    this.createdBy = info;
    return this;
  }

  setOrganisationInfo(info: OrganisationInfo) {
    info.name = info.name.replaceAll('"', "'");
    this.organisationInfo = info;
    return this;
  }

  setAdditionalFields(fields: Nullable<AdditionalRegFields>) {
    this.additionalRegFields = fields;
    return this;
  }

  setPersonInfo(info: RegPersonInfo) {
    this.personInfo = info;
    return this;
  }

  setProducts(products: RegistrationProduct | RegistrationProduct[]) {
    this.products = Array.isArray(products) ? products : [products];
    return this;
  }

  getDefaultPrivacy({ eventPreference, buPreference }: Preferences) {
    return [
      {
        levelId: EventHelper.eventEdition?.eventGbsCode,
        level: "event",
        channelSettings: {
          email: eventPreference,
          phone: eventPreference,
          sms: eventPreference,
          mail: eventPreference,
          thirdParty: eventPreference
        }
      },
      {
        levelId: EventHelper.eventEdition?.businessUnitGbsCode,
        level: "bu",
        channelSettings: {
          email: buPreference,
          phone: buPreference,
          sms: buPreference,
          mail: buPreference
        }
      }
    ];
  }

  getPersonInfoFromBadge(data: INewBadge["personInfo"]) {
    const { email, firstName, lastName, jobTitle, mobilePhone } = data;
    return { email, firstName, jobTitle, lastName, mobilePhone: mobilePhone as unknown as string };
  }

  getRegistrationPayload({ atlasId, badgeLocationUrl, visitorType, gbsCode, locale, participatingOrganisationAtlasCode, orderId, preferences, addonProductCodes }: RegistrationPayload): RegistrationDto {
    const payload: RegistrationDto = {
      eventEditionGbsCode: gbsCode,
      eventEditionAtlasCode: atlasId,
      registrationDate: dayjs(new Date()).format(),
      digitalPlatform: "Atlas",
      badgeLocation: badgeLocationUrl || "https://rxglobal.com/",
      locale,
      addonProductCodes,
      person: this.personInfo,
      privacyResponses: this.getDefaultPrivacy(preferences),
      visitorType,
      organisation: this.organisationInfo,
      source: "onsite",
      referrer: window.location.href,
      createdBy: this.createdBy,
      products: this.products,
      participatingOrganisationAtlasCode,
      badgeNumber: '',
      gbsEnabled: true
    };
    if (orderId) {
      payload.orderId = orderId;
    }
    return payload;
  }

  transformOrgStateToDto(companyInfo: INewBadge["orgInfo"]): OrganisationInfo {
    const { state, address, location, province, postcode, city, companyName, country } = companyInfo;
    const stateField = state || province;
    return {
      address: {
        addressLine1: address,
        addressLine2: StringUtils.removeExtraSpaces(location),
        state: stateField ? StringUtils.removeExtraSpaces(state || province) : undefined,
        city,
        countryIsoCode: country,
        postcode
      },
      name: companyName
    };
  }
}

export default new RegistrationDtoBuilder();
